import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api, formatINR, formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "@/components/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  User, MapPin, Calendar, Download, Plus, Trash2, Loader2,
  Briefcase, CreditCard, Users, Clock, Phone, CalendarDays, XCircle, Wallet, Gift, Star,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  confirmed: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  completed: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  voucher_issued: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
};
const STATUS_LABELS = {
  pending: "Awaiting Payment",
  confirmed: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled",
  voucher_issued: "Voucher Issued",
};

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
      <div className={`p-2.5 rounded-lg ${color}`}><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-2xl font-bold">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

function TripHistoryTab() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();
  const [modifyModal, setModifyModal] = useState(null);
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelPreview, setCancelPreview] = useState(null);
  const [cancelPreviewLoading, setCancelPreviewLoading] = useState(false);
  const [cancelStep, setCancelStep] = useState("preview"); // "preview" | "confirm"
  const [newDate, setNewDate] = useState("");
  const [modifyGuests, setModifyGuests] = useState({ adults: 1, children: 0, infants: 0 });
  const [cancelReason, setCancelReason] = useState("");
  const [acting, setActing] = useState(false);
  const [partialMode, setPartialMode] = useState(false);
  const [cancelGuests, setCancelGuests] = useState({ adults: 0, children: 0, infants: 0 });

  const fetchBookings = () => {
    api.get("/bookings/me").then((r) => setBookings(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchBookings(); }, []);

  const downloadVoucher = async (bookingId) => {
    try {
      const res = await api.get(`/bookings/${bookingId}/voucher`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `voucher-${bookingId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Voucher not available yet");
    }
  };

  const handleModify = async () => {
    if (!modifyModal) return;
    const payload = {};
    if (newDate && newDate !== modifyModal.travel_date) payload.new_travel_date = newDate;
    if (modifyGuests.adults !== modifyModal.adults) payload.new_adults = modifyGuests.adults;
    if (modifyGuests.children !== (modifyModal.children || 0)) payload.new_children = modifyGuests.children;
    if (modifyGuests.infants !== (modifyModal.infants || 0)) payload.new_infants = modifyGuests.infants;
    if (Object.keys(payload).length === 0) { toast.error("No changes to apply"); return; }
    setActing(true);
    try {
      const res = await api.post(`/bookings/${modifyModal.id}/modify`, payload);
      const priceDiff = res.data?.price_diff || 0;
      let msg = "Booking modified successfully!";
      if (priceDiff > 0) msg += ` Additional balance: ${formatINR(priceDiff)}`;
      else if (priceDiff < 0) msg += ` Refund of ${formatINR(Math.abs(priceDiff))} will be processed.`;
      toast.success(msg);
      setModifyModal(null);
      setNewDate("");
      fetchBookings();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setActing(false); }
  };

  const openCancelDialog = async (booking) => {
    setCancelModal(booking);
    setCancelReason("");
    setCancelStep("preview");
    setCancelPreview(null);
    setCancelPreviewLoading(true);
    setPartialMode(false);
    setCancelGuests({ adults: 0, children: 0, infants: 0 });
    try {
      const { data } = await api.get(`/bookings/${booking.id}/cancel-preview`);
      setCancelPreview(data);
    } catch (e) {
      // If preview fails, show a fallback preview so dialog still works
      const totalPaid = booking.payment_status === "paid" ? (booking.total_amount || 0) : (booking.advance_amount || 0);
      setCancelPreview({
        booking_id: booking.id,
        total_paid: totalPaid,
        days_before_travel: null,
        refund_pct: null,
        refund_amount: null,
        cancellation_fee: null,
        fallback: true,
      });
    } finally {
      setCancelPreviewLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelModal) return;
    setActing(true);
    try {
      const payload = { reason: cancelReason };
      if (partialMode && cancelPreview?.allow_partial_cancellation) {
        payload.cancel_guests = cancelGuests;
      }
      const res = await api.post(`/bookings/${cancelModal.id}/cancel`, payload);
      const refund = res.data.refund_amount || 0;
      const isPartial = res.data.partial_cancellation;
      toast.success(isPartial
        ? `Guests reduced. Refund of ${formatINR(refund)} will be processed.`
        : refund > 0 ? `Booking cancelled. Refund of ${formatINR(refund)} will be processed.` : "Booking cancelled.");
      setCancelModal(null);
      setCancelReason("");
      setCancelPreview(null);
      setPartialMode(false);
      fetchBookings();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setActing(false); }
  };

  const handlePayBalance = async (booking) => {
    setActing(true);
    try {
      const { data: order } = await api.post(`/bookings/${booking.id}/pay-balance`);
      if (order.mode === "razorpay" && window.Razorpay) {
        const options = {
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          name: settings.site_name || "HyderabadTourism.in",
          description: `Balance payment for ${booking.reference_no}`,
          order_id: order.order_id,
          handler: async (response) => {
            try {
              await api.post(`/bookings/${booking.id}/verify-balance`, {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              });
              toast.success("Balance paid successfully!");
              fetchBookings();
            } catch (err) {
              toast.error("Payment verification failed");
            }
          },
          prefill: { name: booking.contact_name, email: booking.contact_email, contact: booking.contact_phone },
          theme: { color: "#0d9488" },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        // Mock fallback
        await api.post(`/bookings/${booking.id}/verify-balance`, {
          razorpay_payment_id: `mock_${Date.now()}`,
          razorpay_order_id: order.order_id,
          razorpay_signature: "mock",
        });
        toast.success("Balance paid successfully!");
        fetchBookings();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setActing(false); }
  };

  if (loading) return <div className="space-y-3">{[1,2,3].map(i=><div key={i} className="h-24 bg-muted animate-pulse rounded-xl" />)}</div>;
  if (!bookings.length) return <div className="text-center py-12 text-muted-foreground"><Briefcase className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No bookings yet. Start exploring!</p></div>;

  return (
    <>
      <div className="space-y-3" data-testid="trip-history">
        {bookings.map((b) => {
          const canModify = ["confirmed", "voucher_issued"].includes(b.status);
          const canCancel = !["cancelled", "refunded"].includes(b.status);
          const canPayBalance = b.payment_status === "advance_paid" && b.balance_amount > 0;
          return (
            <Card key={b.id} className="p-4" data-testid={`booking-${b.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-semibold text-sm truncate">{b.product_title}</span>
                    <Badge className={`${STATUS_COLORS[b.status] || STATUS_COLORS.pending} text-[10px]`}>{STATUS_LABELS[b.status] || b.status}</Badge>
                    {b.payment_status === "advance_paid" && (
                      <Badge variant="outline" className="text-[10px] text-saffron border-saffron">Balance Due</Badge>
                    )}
                    {b.refund_amount > 0 && (
                      <Badge variant="outline" className="text-[10px] text-green-600 border-green-600">Refund: {formatINR(b.refund_amount)}</Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-1">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{b.travel_date}</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{b.adults}A{b.children > 0 ? ` ${b.children}C` : ""}</span>
                    <span className="flex items-center gap-1"><CreditCard className="w-3 h-3" />{formatINR(b.total_amount)}</span>
                    {b.pickup_location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{b.pickup_location}</span>}
                  </div>
                  {canPayBalance && (
                    <div className="mt-2 text-xs bg-saffron/10 text-saffron px-3 py-1.5 rounded-lg inline-flex items-center gap-2">
                      Advance: {formatINR(b.advance_amount)} &middot; Balance: <strong>{formatINR(b.balance_amount)}</strong>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  {b.status !== "pending" ? (
                    <span className="text-xs text-muted-foreground" data-testid={`ref-${b.id}`}>Ticket #{b.reference_no}</span>
                  ) : (
                    <span className="text-xs text-yellow-600 font-medium" data-testid={`unpaid-${b.id}`}>Unpaid — Complete payment</span>
                  )}
                  <div className="flex flex-wrap gap-1 justify-end">
                    {b.status === "pending" && b.payment_status === "pending" && (
                      <Button variant="default" size="sm" onClick={() => handlePayBalance(b)} disabled={acting} className="bg-saffron hover:bg-saffron-dark text-white" data-testid={`pay-now-${b.id}`}>
                        <Wallet className="w-3 h-3 mr-1" /> Pay Now
                      </Button>
                    )}
                    {canPayBalance && (
                      <Button variant="default" size="sm" onClick={() => handlePayBalance(b)} disabled={acting} className="bg-saffron hover:bg-saffron-dark text-white" data-testid={`pay-balance-${b.id}`}>
                        <Wallet className="w-3 h-3 mr-1" /> Pay Balance
                      </Button>
                    )}
                    {(b.status === "confirmed" || b.status === "voucher_issued") && (
                      <Button variant="outline" size="sm" onClick={() => downloadVoucher(b.id)} data-testid={`download-voucher-${b.id}`}>
                        <Download className="w-3 h-3 mr-1" /> Voucher
                      </Button>
                    )}
                    {canModify && (
                      <Button variant="outline" size="sm" onClick={() => {
                        setModifyModal(b);
                        setNewDate(b.travel_date || "");
                        setModifyGuests({ adults: b.adults || 1, children: b.children || 0, infants: b.infants || 0 });
                      }} data-testid={`modify-btn-${b.id}`}>
                        <CalendarDays className="w-3 h-3 mr-1" /> Modify
                      </Button>
                    )}
                    {canCancel && (
                      <Button variant="outline" size="sm" className="text-destructive border-destructive/30" onClick={() => openCancelDialog(b)} data-testid={`cancel-btn-${b.id}`}>
                        <XCircle className="w-3 h-3 mr-1" /> Cancel
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Modify Booking Dialog */}
      <Dialog open={!!modifyModal} onOpenChange={(v) => !v && setModifyModal(null)}>
        <DialogContent data-testid="modify-booking-dialog" className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Modify Booking</DialogTitle></DialogHeader>
          {modifyModal && (
            <div className="space-y-4 mt-2">
              <div className="bg-muted/50 p-3 rounded-lg text-sm">
                <p><strong>{modifyModal.product_title}</strong></p>
                <p className="text-muted-foreground">Ref: {modifyModal.reference_no} · Current: {modifyModal.travel_date}</p>
              </div>

              {/* Date Change */}
              <div>
                <Label>Travel Date</Label>
                <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  data-testid="new-travel-date" />
              </div>

              {/* Guest Count Changes */}
              <div className="border border-border rounded-lg p-3 space-y-3">
                <Label className="font-semibold text-sm">Guests</Label>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Adults</Label>
                    <div className="flex items-center gap-1 mt-1">
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8"
                        onClick={() => setModifyGuests(g => ({ ...g, adults: Math.max(1, g.adults - 1) }))}
                        data-testid="mod-adults-minus">-</Button>
                      <span className="w-8 text-center font-semibold" data-testid="mod-adults-count">{modifyGuests.adults}</span>
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8"
                        onClick={() => setModifyGuests(g => ({ ...g, adults: g.adults + 1 }))}
                        data-testid="mod-adults-plus">+</Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Children</Label>
                    <div className="flex items-center gap-1 mt-1">
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8"
                        onClick={() => setModifyGuests(g => ({ ...g, children: Math.max(0, g.children - 1) }))}
                        data-testid="mod-children-minus">-</Button>
                      <span className="w-8 text-center font-semibold" data-testid="mod-children-count">{modifyGuests.children}</span>
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8"
                        onClick={() => setModifyGuests(g => ({ ...g, children: g.children + 1 }))}
                        data-testid="mod-children-plus">+</Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Infants</Label>
                    <div className="flex items-center gap-1 mt-1">
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8"
                        onClick={() => setModifyGuests(g => ({ ...g, infants: Math.max(0, g.infants - 1) }))}
                        data-testid="mod-infants-minus">-</Button>
                      <span className="w-8 text-center font-semibold" data-testid="mod-infants-count">{modifyGuests.infants}</span>
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8"
                        onClick={() => setModifyGuests(g => ({ ...g, infants: g.infants + 1 }))}
                        data-testid="mod-infants-plus">+</Button>
                    </div>
                  </div>
                </div>
                {(modifyGuests.adults !== (modifyModal.adults || 1) || modifyGuests.children !== (modifyModal.children || 0) || modifyGuests.infants !== (modifyModal.infants || 0)) && (
                  <p className="text-xs text-blue-600">Guest count changed — price will be recalculated</p>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setModifyModal(null)}>Cancel</Button>
                <Button onClick={handleModify} disabled={acting} data-testid="confirm-modify" className="bg-teal hover:bg-teal/90 text-white">
                  {acting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog — Two-Step: Preview → Confirm */}
      <Dialog open={!!cancelModal} onOpenChange={(v) => { if (!v) { setCancelModal(null); setCancelPreview(null); setPartialMode(false); } }}>
        <DialogContent data-testid="cancel-booking-dialog" className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Cancel Booking</DialogTitle></DialogHeader>
          {cancelModal && (
            <div className="space-y-4 mt-2">
              {/* Booking Summary */}
              <div className="bg-muted/50 p-3 rounded-lg text-sm">
                <p><strong>{cancelModal.product_title}</strong></p>
                <p className="text-muted-foreground">Date: {cancelModal.travel_date} · Amount: {formatINR(cancelModal.total_amount)}</p>
              </div>

              {/* Loading Preview */}
              {cancelPreviewLoading && (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">Calculating refund...</span>
                </div>
              )}

              {/* Step 1: Refund Preview */}
              {cancelPreview && cancelStep === "preview" && (
                <>
                  {cancelPreview.fallback ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg text-sm text-yellow-800 dark:text-yellow-200">
                      <strong>Cancellation Policy:</strong>
                      <p className="mt-1">Contact support for cancellation details.</p>
                    </div>
                  ) : (
                    <>
                      {/* Product-specific cancellation tiers */}
                      {cancelPreview.cancellation_tiers && cancelPreview.cancellation_tiers.length > 0 && (
                        <div className="bg-muted/40 p-3 rounded-lg text-xs" data-testid="cancellation-tiers">
                          <strong className="text-sm">Cancellation Policy</strong>
                          <ul className="list-disc ml-4 mt-1.5 space-y-0.5">
                            {cancelPreview.cancellation_tiers.map((tier, i) => (
                              <li key={i} className={cancelPreview.matched_rule === tier.label ? "font-semibold text-foreground" : "text-muted-foreground"}>
                                {tier.label}: <span className={tier.refund_pct === 100 ? "text-green-600" : tier.refund_pct > 0 ? "text-yellow-600" : "text-red-600"}>{tier.refund_pct}% refund</span>
                                {cancelPreview.matched_rule === tier.label && <span className="ml-1 text-primary">(applies to you)</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Custom policy text from product */}
                      {cancelPreview.cancellation_policy_text && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-xs text-blue-800 dark:text-blue-200">
                          <strong>Terms:</strong> {cancelPreview.cancellation_policy_text}
                        </div>
                      )}

                      {/* Refund estimate */}
                      <div className="border border-border rounded-lg overflow-hidden" data-testid="refund-preview">
                        <div className="bg-muted/30 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b border-border">Refund Estimate</div>
                        <div className="p-4 space-y-3">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Days until travel</span>
                            <span className="font-medium">{cancelPreview.days_before_travel != null ? `${cancelPreview.days_before_travel} days` : "N/A"}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Amount paid</span>
                            <span className="font-medium">{formatINR(cancelPreview.total_paid)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Refund percentage</span>
                            <span className={`font-semibold ${cancelPreview.refund_pct === 100 ? "text-green-600" : cancelPreview.refund_pct > 0 ? "text-yellow-600" : "text-red-600"}`}>
                              {cancelPreview.refund_pct}%
                            </span>
                          </div>
                          {cancelPreview.matched_rule && (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Applied rule</span>
                              <span className="font-medium text-primary text-xs">{cancelPreview.matched_rule}</span>
                            </div>
                          )}
                          <hr className="border-border" />
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Cancellation fee</span>
                            <span className="font-medium text-red-600">{formatINR(cancelPreview.cancellation_fee)}</span>
                          </div>
                          <div className="flex justify-between text-base font-bold">
                            <span>Your refund</span>
                            <span className={cancelPreview.refund_amount > 0 ? "text-green-600" : "text-red-600"}>
                              {formatINR(cancelPreview.refund_amount)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {cancelPreview.refund_pct === 0 && (
                        <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
                          <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
                          <span>This booking is <strong>non-refundable</strong> based on the cancellation policy.</span>
                        </div>
                      )}

                      {/* Partial cancellation option */}
                      {cancelPreview.allow_partial_cancellation && cancelPreview.total_guests > 1 && (
                        <div className="border border-border rounded-lg p-3 space-y-3" data-testid="partial-cancel-section">
                          <div className="flex items-center gap-2">
                            <input type="checkbox" id="partial-mode" checked={partialMode} onChange={(e) => {
                              setPartialMode(e.target.checked);
                              if (!e.target.checked) setCancelGuests({ adults: 0, children: 0, infants: 0 });
                            }} className="rounded" data-testid="partial-mode-toggle" />
                            <label htmlFor="partial-mode" className="text-sm font-medium cursor-pointer">Cancel only some guests (partial cancellation)</label>
                          </div>
                          {partialMode && (
                            <div className="grid grid-cols-3 gap-2 text-sm">
                              {cancelPreview.adults > 0 && (
                                <div>
                                  <Label className="text-xs">Adults to cancel (of {cancelPreview.adults})</Label>
                                  <Input type="number" min={0} max={cancelPreview.adults} value={cancelGuests.adults}
                                    onChange={(e) => setCancelGuests(g => ({ ...g, adults: Math.min(Number(e.target.value) || 0, cancelPreview.adults) }))}
                                    data-testid="cancel-adults-count" />
                                </div>
                              )}
                              {cancelPreview.children > 0 && (
                                <div>
                                  <Label className="text-xs">Children to cancel (of {cancelPreview.children})</Label>
                                  <Input type="number" min={0} max={cancelPreview.children} value={cancelGuests.children}
                                    onChange={(e) => setCancelGuests(g => ({ ...g, children: Math.min(Number(e.target.value) || 0, cancelPreview.children) }))}
                                    data-testid="cancel-children-count" />
                                </div>
                              )}
                              {cancelPreview.infants > 0 && (
                                <div>
                                  <Label className="text-xs">Infants to cancel (of {cancelPreview.infants})</Label>
                                  <Input type="number" min={0} max={cancelPreview.infants} value={cancelGuests.infants}
                                    onChange={(e) => setCancelGuests(g => ({ ...g, infants: Math.min(Number(e.target.value) || 0, cancelPreview.infants) }))}
                                    data-testid="cancel-infants-count" />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => setCancelModal(null)} data-testid="cancel-dialog-close">Keep Booking</Button>
                    <Button variant="destructive" onClick={() => setCancelStep("confirm")} data-testid="proceed-cancel">
                      {partialMode ? "Proceed with Partial Cancel" : "Proceed to Cancel"}
                    </Button>
                  </div>
                </>
              )}

              {/* Step 2: Reason + Final Confirm */}
              {cancelPreview && cancelStep === "confirm" && (
                <>
                  <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg text-sm text-red-700 dark:text-red-300">
                    {partialMode ? (
                      <>Reducing guests: {cancelGuests.adults > 0 && `${cancelGuests.adults} adult(s) `}{cancelGuests.children > 0 && `${cancelGuests.children} child(ren) `}{cancelGuests.infants > 0 && `${cancelGuests.infants} infant(s)`}. Refund will be calculated proportionally at <strong>{cancelPreview.refund_pct}%</strong>.</>
                    ) : (
                      <>You will receive <strong>{formatINR(cancelPreview.refund_amount)}</strong> as refund ({cancelPreview.refund_pct}%).
                      {cancelPreview.cancellation_fee > 0 && <> Cancellation fee: <strong>{formatINR(cancelPreview.cancellation_fee)}</strong>.</>}</>
                    )}
                  </div>
                  <div>
                    <Label>Reason (optional)</Label>
                    <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={2} placeholder="Why are you cancelling?" data-testid="cancel-reason" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => setCancelStep("preview")} data-testid="back-to-preview">Back</Button>
                    <Button variant="destructive" onClick={handleCancel} disabled={acting} data-testid="confirm-cancel">
                      {acting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} {partialMode ? "Confirm Partial Cancel" : "Yes, Cancel Booking"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function SavedTravellersTab() {
  const [travellers, setTravellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", age: "", type: "adult", id_type: "", id_number: "", phone: "", relation: "self" });
  const [saving, setSaving] = useState(false);

  const fetch = () => api.get("/profile/travellers").then((r) => { setTravellers(r.data); setLoading(false); });
  useEffect(() => { fetch(); }, []);

  const add = async () => {
    if (!draft.name.trim()) return;
    setSaving(true);
    try {
      await api.post("/profile/travellers", { ...draft, age: Number(draft.age) || null });
      setDraft({ name: "", age: "", type: "adult", id_type: "", id_number: "", phone: "", relation: "self" });
      setAdding(false);
      fetch();
      toast.success("Traveller saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/profile/travellers/${id}`);
    setTravellers(travellers.filter((t) => t.id !== id));
    toast.success("Removed");
  };

  if (loading) return <div className="h-24 bg-muted animate-pulse rounded-xl" />;

  return (
    <div className="space-y-3" data-testid="saved-travellers">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Save frequent traveller details to speed up booking.</p>
        <Button variant="outline" size="sm" onClick={() => setAdding(!adding)} data-testid="add-traveller-btn">
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>

      {adding && (
        <Card className="p-4 space-y-3 border-primary/30" data-testid="add-traveller-form">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="col-span-2"><Label>Full Name *</Label><Input value={draft.name} onChange={(e) => setDraft({...draft, name: e.target.value})} data-testid="traveller-name" /></div>
            <div><Label>Age</Label><Input type="number" value={draft.age} onChange={(e) => setDraft({...draft, age: e.target.value})} /></div>
            <div>
              <Label>Type</Label>
              <Select value={draft.type} onValueChange={(v) => setDraft({...draft, type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="adult">Adult</SelectItem>
                  <SelectItem value="child">Child</SelectItem>
                  <SelectItem value="infant">Infant</SelectItem>
                  <SelectItem value="senior">Senior</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Phone</Label><Input value={draft.phone} onChange={(e) => setDraft({...draft, phone: e.target.value})} /></div>
            <div><Label>ID Type</Label><Input value={draft.id_type} onChange={(e) => setDraft({...draft, id_type: e.target.value})} placeholder="Aadhaar / Passport" /></div>
            <div><Label>ID Number</Label><Input value={draft.id_number} onChange={(e) => setDraft({...draft, id_number: e.target.value})} /></div>
            <div>
              <Label>Relation</Label>
              <Select value={draft.relation} onValueChange={(v) => setDraft({...draft, relation: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">Self</SelectItem>
                  <SelectItem value="spouse">Spouse</SelectItem>
                  <SelectItem value="child">Child</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                  <SelectItem value="friend">Friend</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>Cancel</Button>
            <Button size="sm" onClick={add} disabled={saving || !draft.name.trim()} data-testid="save-traveller-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Save
            </Button>
          </div>
        </Card>
      )}

      {travellers.length === 0 && !adding ? (
        <div className="text-center py-8 text-muted-foreground">
          <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No saved travellers yet</p>
        </div>
      ) : (
        travellers.map((t) => (
          <Card key={t.id} className="p-3 flex items-center gap-3" data-testid={`traveller-${t.id}`}>
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <User className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">{t.name}</div>
              <div className="text-xs text-muted-foreground">
                {t.type} {t.age ? `· ${t.age}y` : ""} {t.relation && t.relation !== "self" ? `· ${t.relation}` : ""}
                {t.phone && <span className="ml-1">· {t.phone}</span>}
              </div>
            </div>
            <button onClick={() => remove(t.id)} className="text-destructive hover:opacity-70 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
          </Card>
        ))
      )}
    </div>
  );
}

function LoyaltyPointsTab() {
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/loyalty/balance"),
      api.get("/loyalty/config"),
    ]).then(([bRes, cRes]) => {
      setData(bRes.data);
      setConfig(cRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="h-24 bg-muted animate-pulse rounded-xl" />;

  const points = data?.points || 0;
  const transactions = data?.transactions || [];
  const redeemValue = config ? (points * config.point_value_inr) : 0;

  return (
    <div className="space-y-4" data-testid="loyalty-points-tab">
      {/* Points Balance Card */}
      <Card className="p-6 bg-gradient-to-br from-teal/5 to-saffron/5 border-teal/20">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-saffron/10 flex items-center justify-center">
            <Star className="w-7 h-7 text-saffron" />
          </div>
          <div>
            <div className="text-3xl font-bold text-teal" data-testid="loyalty-balance">{points.toLocaleString()}</div>
            <div className="text-sm text-muted-foreground">Loyalty Points</div>
          </div>
          {redeemValue > 0 && (
            <div className="ml-auto text-right">
              <div className="text-lg font-semibold text-saffron" data-testid="loyalty-value">{formatINR(redeemValue)}</div>
              <div className="text-xs text-muted-foreground">Redeemable value</div>
            </div>
          )}
        </div>
        {config && (
          <div className="mt-4 flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span className="bg-white/60 px-2 py-1 rounded-md">Earn {config.points_per_100_inr} pts / {formatINR(100)} spent</span>
            <span className="bg-white/60 px-2 py-1 rounded-md">1 pt = {formatINR(config.point_value_inr)}</span>
            <span className="bg-white/60 px-2 py-1 rounded-md">Min {config.min_redeem} pts to redeem</span>
          </div>
        )}
      </Card>

      {/* Transaction History */}
      <h3 className="font-semibold text-sm">Transaction History</h3>
      {transactions.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Gift className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No points earned yet. Book a trip to start earning!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {[...transactions].reverse().slice(0, 20).map((t, i) => (
            <Card key={i} className="p-3 flex items-center gap-3" data-testid={`loyalty-txn-${i}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                t.type === "earn" ? "bg-green-100 text-green-600" : "bg-saffron/10 text-saffron"
              }`}>
                {t.type === "earn" ? <Plus className="w-4 h-4" /> : <Gift className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{t.description}</div>
                <div className="text-xs text-muted-foreground">{new Date(t.at).toLocaleDateString()}</div>
              </div>
              <div className={`font-semibold text-sm ${t.type === "earn" ? "text-green-600" : "text-saffron"}`}>
                {t.type === "earn" ? "+" : ""}{t.points} pts
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [stats, setStats] = useState({});
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);

  useEffect(() => {
    api.get("/profile/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/loyalty/balance").then((r) => setLoyaltyPoints(r.data.points || 0)).catch(() => {});
  }, []);

  const breadcrumbItems = [{ label: "Home", to: "/" }, { label: "My Profile" }];

  return (
    <div data-testid="profile-page">
      <SEO title="My Profile" description="Manage your bookings, saved travellers, and account settings" schemas={[buildBreadcrumbSchema(breadcrumbItems)]} />

      <section className="bg-gradient-to-b from-secondary/40 to-background pt-8 pb-4">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />
          <div className="flex items-center gap-4 mt-2">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h1 className="font-display text-2xl sm:text-3xl">{user?.name || "My Account"}</h1>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="profile-stats">
          <StatCard icon={Briefcase} label="Total Bookings" value={stats.total_bookings || 0} color="bg-blue-100 dark:bg-blue-900/30 text-blue-600" />
          <StatCard icon={Clock} label="Confirmed" value={stats.confirmed || 0} color="bg-green-100 dark:bg-green-900/30 text-green-600" />
          <StatCard icon={Star} label="Loyalty Points" value={loyaltyPoints.toLocaleString()} color="bg-saffron/10 text-saffron" />
          <StatCard icon={CreditCard} label="Total Spent" value={formatINR(stats.total_spent || 0)} color="bg-purple-100 dark:bg-purple-900/30 text-purple-600" />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="trips" className="w-full">
          <TabsList className="w-full sm:w-auto mb-4" data-testid="profile-tabs">
            <TabsTrigger value="trips" data-testid="tab-trips">Trip History</TabsTrigger>
            <TabsTrigger value="points" data-testid="tab-points">Loyalty Points</TabsTrigger>
            <TabsTrigger value="travellers" data-testid="tab-travellers">Saved Travellers</TabsTrigger>
          </TabsList>

          <TabsContent value="trips">
            <TripHistoryTab />
          </TabsContent>

          <TabsContent value="points">
            <LoyaltyPointsTab />
          </TabsContent>

          <TabsContent value="travellers">
            <SavedTravellersTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
